export type CsvCell = string | number | boolean | null | undefined;

const CSV_ROW_SEPARATOR = "\r\n";
const UTF8_BOM = "﻿";

// Leading characters a spreadsheet interprets as the start of a formula
const FORMULA_PREFIXES = ["=", "+", "-", "@", "\t", "\r"];

// Neutralizes formula injection first, then applies RFC 4180 quoting
const escapeCell = (value: CsvCell): string => {
  if (value === null || value === undefined) {
    return "";
  }

  let field = String(value);
  if (field === "") {
    return "";
  }

  if (FORMULA_PREFIXES.includes(field.charAt(0))) {
    field = `'${field}`;
  }

  if (/[",\r\n]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`;
  }

  return field;
};

/**
 * Serialize rows of values into an RFC 4180 CSV string
 * Null, undefined and empty values are exported as empty cells
 * @param {CsvCell[][]} rows - Rows to serialize, the first one usually being the header
 * @returns {string} CSV content, without the UTF-8 BOM
 */
export const toCsv = (rows: CsvCell[][]): string => {
  return rows
    .map(row => row.map(escapeCell).join(","))
    .join(CSV_ROW_SEPARATOR);
};

/**
 * Format a date as an ISO 8601 UTC string for CSV exports
 * Locale-independent, unlike printDate which is meant for on-screen rendering
 * @param {string | null | undefined} value - Date to format
 * @returns {string} ISO 8601 UTC date, or an empty string if the value is missing or invalid
 */
export const toIsoUtc = (value: string | null | undefined): string => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString();
};

/**
 * Trigger the download of a CSV file in the browser
 * The UTF-8 BOM is prepended so Excel detects the encoding
 * @param {string} filename - Name of the downloaded file
 * @param {string} csv - CSV content, as returned by toCsv
 * @returns {void}
 */
export const downloadCsv = (filename: string, csv: string): void => {
  const blob = new Blob([UTF8_BOM, csv], {
    type: "text/csv;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
};
